import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { getCollector } from 'src/apiHelper/collectorApi'
import moment from 'moment'
import { format } from 'date-fns'
import { CCard, CCardBody, CCol, CRow } from '@coreui/react-pro'
import PatientList from './Components/CensusCalender/PatientList/PatientList'
import './CensusCalendar.scss'
import Button from 'src/components/common/Button/Button'
import './Dashboard.scss'
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { getVisitsCensus } from 'src/redux/census/censusThunk'
import { setActiveDate, setActiveFacilityId } from 'src/redux/census/censusSlice'
import DashboardCalendar from './Components/DashboardCalendar'
import FacilityDropdown from 'src/components/common/SearchableDropdown/FacilityDropdown'
import CommonChart from './TodayFacilityInfo/CommonChart'
import { COLLECTOR_URL } from '../../../apiHelper/apiRoute/collector'
import { GRAY_300, GREEN_300, RED_300 } from '../../../scss/colorConsts'
import { convertHyphenDateToSlashDate } from '../../../utils/sharedFunctions'
import { userSettingPut } from '../../../redux/user/userThunk'
import { STATUS_GROUPS, VISIT_STATUS } from '../../../utils/statusConstants'

const CensusCalender = ({ patients, selectedFacility }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [data, setData] = useState([])

  const allFacilitiesList = useSelector((state) => state?.census?.allFacilitiesList) || []
  const dischargeCodes = useSelector((state) => state.census?.dischargeCodes) || []
  const thisDate = useSelector((state) => state?.census?.activeDate)
  const slashThisDate = new Date(convertHyphenDateToSlashDate(thisDate))

  const provider = useSelector((state) => state?.user?.provider) || {}
  const settings = useSelector((state) => state.user?.settings)

  const [firstLastDate, setFirstLastDate] = useState({
    firstDate: new Date(slashThisDate.getFullYear(), slashThisDate.getMonth(), 1),
    lastDate: new Date(slashThisDate.getFullYear(), slashThisDate.getMonth() + 1, 0),
  })

  useEffect(() => {
    setFirstLastDate({
      firstDate: new Date(slashThisDate.getFullYear(), slashThisDate.getMonth(), 1),
      lastDate: new Date(slashThisDate.getFullYear(), slashThisDate.getMonth() + 1, 0),
    })
  }, [thisDate])

  const [activeDates, setActiveDates] = useState(new Date())

  const handleMonthChangeFunction = (args) => {
    setFirstLastDate({ firstDate: args.firstDay, lastDate: args.lastDay })
  }

  useEffect(() => {
    let url = COLLECTOR_URL.DAILY_SUMMARY.replace(
      '{startDate}',
      moment(firstLastDate.firstDate).format('YYYY-MM-DD'),
    ).replace('{endDate}', moment(firstLastDate.lastDate).format('YYYY-MM-DD'))

    getCollector(url)
      .then((res) => {
        setData(res?.status === 'success' ? res.result : [])
      })
      .catch(() => setData([]))
  }, [firstLastDate])

  const completedList = patients?.filter(
    (item) =>
      item.facility_id === selectedFacility?._id && item?.statusGroup === STATUS_GROUPS.completed,
  )
  const inCompletedList = patients?.filter(
    (item) =>
      item.facility_id === selectedFacility?._id && item?.statusGroup === STATUS_GROUPS.in_complete,
  )

  const handleDateChange = (date) => {
    const formattedDate = format(date?.date, 'yyyy-MM-dd')
    dispatch(setActiveDate(formattedDate))
    dispatch(
      getVisitsCensus({
        servicedate: formattedDate,
        thisProvider: provider,
        facility_id: selectedFacility?._id,
      }),
    )

    setActiveDates(date?.date)
  }

  const handleFacilityActive = (id) => {
    const facility = allFacilitiesList.find((item) => item._id === id)
    if (facility) {
      dispatch(setActiveFacilityId(id))
      let newUserSettings = {
        ...settings,
        censusFacilityID: id,
      }
      dispatch(userSettingPut(newUserSettings))
    }
  }

  return (
    <div className="calendar-container">
      <CRow className="align-items-center mb-2 justify-content-between">
        <CCol sm="3">
          <span className="calendar-heading">Census calendar</span>
        </CCol>
        <CCol xsm="auto">
          <FacilityDropdown
            options={allFacilitiesList}
            selectedVal={selectedFacility}
            handleChange={(fac) => handleFacilityActive(fac?._id)}
            showStars={true}
          />
        </CCol>
        <CCol sm="3">
          <Button
            color="primary"
            onClick={() => {
              navigate('/census')
            }}
          >
            Go to census
          </Button>
        </CCol>
      </CRow>

      <CRow className="mb-3">
        <CCol className="mb-3">
          <CommonChart
            title={'Available'}
            patients={patients?.filter((item) => item?.statusGroup === STATUS_GROUPS.available)}
          />
        </CCol>
        <CCol className="mb-3">
          <CommonChart
            title={'In Progress'}
            patients={patients?.filter((item) => item?.statusGroup === STATUS_GROUPS.in_complete)}
          />
        </CCol>
        <CCol className="mb-3">
          <CommonChart
            title={'Completed'}
            patients={patients?.filter((item) => item?.statusGroup === STATUS_GROUPS.completed)}
          />
        </CCol>
        <CCol className="mb-3">
          <CommonChart
            title={'Skipped'}
            patients={patients?.filter((item) => item?.status === VISIT_STATUS.not_seen)}
          />
        </CCol>
      </CRow>

      <CCard className="graph-card mb-3">
        <CCardBody>
          <CRow>
            <CCol sm="6">
              <div className="">
                <DashboardCalendar
                  data={data}
                  handleMonthChange={handleMonthChangeFunction}
                  facility_id={selectedFacility?._id}
                  handleDateChange={handleDateChange}
                />

                <div className="circle-progress-info">
                  <div className="progress-holder">
                    <div className="progress-el">
                      <CircularProgressbar
                        value={completedList?.length}
                        maxValue={completedList?.length + inCompletedList?.length}
                        text={`${completedList?.length}/${
                          completedList?.length + inCompletedList?.length
                        }`}
                        styles={buildStyles({
                          pathColor: completedList?.length > 0 ? GREEN_300 : GRAY_300,
                          textColor: 'var(--default-text-color)',
                          trailColor:
                            completedList?.length + inCompletedList?.length > 0
                              ? RED_300
                              : GRAY_300,
                        })}
                      />
                      <p className="progress-txt">
                        {
                          patients?.filter((item) => item.facility_id === selectedFacility?._id)
                            ?.length
                        }{' '}
                        total
                      </p>
                    </div>
                  </div>
                  <div className="info-holder align-self-center">
                    <div className="tittle">{selectedFacility?.name}</div>
                    <div className="txt">
                      <p className="fs-8 mb-0">
                        {selectedFacility?.place_of_service_name
                          ? selectedFacility?.place_of_service_name
                          : ''}
                      </p>
                      {(selectedFacility?.address?.street_line_1 ||
                        selectedFacility?.address?.city ||
                        selectedFacility?.address?.state ||
                        selectedFacility?.address?.zip) && (
                        <p className="fs-8">
                          {selectedFacility?.address?.street_line_1}
                          <br />
                          {selectedFacility?.address
                            ? selectedFacility?.address?.city +
                              ', ' +
                              selectedFacility?.address?.state +
                              ' ' +
                              (selectedFacility?.address?.zip).slice(0, 5) +
                              '-' +
                              (selectedFacility?.address?.zip).slice(5)
                            : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CCol>
            <CCol sm="6">
              <PatientList
                patients={patients}
                selectedFacility={selectedFacility}
                activeDate={activeDates}
                dischargeCodes={dischargeCodes}
              />
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  )
}

CensusCalender.propTypes = {
  patients: PropTypes.array,
  selectedFacility: PropTypes.object,
}

export default CensusCalender
